import React, { useCallback, useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';
import Quagga from '@ericblade/quagga2';
import { findDesiredCamera, useRafLoop } from "../Util.js";
const styles = {
  containerStyle: {
    backgroundColor: 'hsl(0, 0%, 0%)',
    width: 400,
    height: 400
  },
  videoStyle: {
    display: 'none',
    width: 400,
    height: 400
  }
};

// Available readers:
// 'code_128_reader',
// 'ean_reader',
// 'ean_5_reader',
// 'ean_2_reader',
// 'ean_8_reader',
// 'code_39_reader',
// 'code_39_vin_reader',
// 'codabar_reader',
// 'upc_reader',
// 'upc_e_reader',
// 'i2of5_reader',
// '2of5_reader',
// 'code_93_reader',
// 'code_32_reader'

/**
 * A component that renders a mixed code scanner using the device's camera.
 * @param {Object} props - The component props.
 * @param {number} [props.width=400] - The width of the scanner.
 * @param {number} [props.height=400] - The height of the scanner.
 * @param {function} props.onScan - A callback function that is called when a code is successfully scanned.
 * @param {function} props.onError - A callback function that is called when an error occurs while accessing the camera.
 * @param {boolean} [props.showStaticImage=false] - A flag that indicates whether to show a static image instead of the camera feed.
 * @param {number} [props.scanInterval=600] - The interval in milliseconds between code scans.
 * @param {number} [props.renderInterval=100] - The interval in milliseconds between rendering updates.
 * @param {string} [props.eventData] - The event data that is passed to the onScan callback function.
 * @param {boolean} [props.scanning] - A flag that indicates whether the scanner is currently scanning.
 * @param {string} [props.inversionAttempts='attemptBoth'] - The inversion attempts to be used when decoding the image file.
 * @param {Array} [props.readers] - An array of readers to use for the scanner.
 * @returns {JSX.Element} A JSX element that renders the mixed code scanner.
 */

function MixedCodeScanner({
  width = 400,
  height = 400,
  onScan,
  onError,
  showStaticImage,
  scanInterval = 600,
  renderInterval = 100,
  eventData,
  scanning,
  readers = ['code_128_reader', 'ean_reader'],
  inversionAttempts = 'attemptBoth'
}) {
  const refVideo = useRef();
  const refCanvas = useRef();
  const refHasVideo = useRef(false);
  const [deviceSize, setDeviceSize] = useState({
    w: width,
    h: height
  });
  const refLastScanTime = useRef(0);
  const performCodeScan = () => {
    if (refHasVideo.current && refCanvas.current) {
      const canvasEl = refCanvas.current;
      const context = canvasEl.getContext('2d');
      const imageData = context.getImageData(0, 0, canvasEl.width, canvasEl.height);
      const qrCode = jsQR(imageData.data, canvasEl.width, canvasEl.height, {
        inversionAttempts
      });
      if (qrCode != null) {
        onScan({
          data: eventData,
          value: {
            text: qrCode.data
          }
        });
        return;
      }
      Quagga.decodeSingle({
        src: canvasEl.toDataURL(),
        numOfWorkers: 0,
        inputStream: {
          size: canvasEl.width
        },
        decoder: {
          readers
        }
      }, result => {
        if (result?.codeResult != null) {
          onScan({
            data: eventData,
            value: {
              text: result.codeResult.code
            }
          });
        }
      });
    }
  };
  useRafLoop(() => {
    if (showStaticImage) {
      return;
    }
    if (refHasVideo.current) {
      const context = refCanvas.current.getContext('2d');
      if (width <= deviceSize.w && height <= deviceSize.h) {
        context.drawImage(refVideo.current, (width - deviceSize.w) * 0.5, (height - deviceSize.h) * 0.5, deviceSize.w, deviceSize.h);
      } else if (width / height < deviceSize.w / deviceSize.h) {
        const scaledDeviceHeight = height;
        const scaledDeviceWidth = deviceSize.w * (height / deviceSize.h);
        context.drawImage(refVideo.current, (width - scaledDeviceWidth) * 0.5, 0, scaledDeviceWidth, scaledDeviceHeight);
      } else {
        const scaledDeviceWidth = width;
        const scaledDeviceHeight = deviceSize.h * (width / deviceSize.w);
        context.drawImage(refVideo.current, 0, (height - scaledDeviceHeight) * 0.5, scaledDeviceWidth, scaledDeviceHeight);
      }
      const now = Date.now();
      const passedMs = now - refLastScanTime.current;
      const scanInt = scanInterval ?? 600;
      if (passedMs > scanInt) {
        performCodeScan();
        refLastScanTime.current = now;
      }
    }
  });
  const loadCamera = useCallback(async () => {
    try {
      const desiredCameraId = await findDesiredCamera();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: desiredCameraId,
          facingMode: 'environment'
        },
        audio: false
      });
      if (refVideo.current) {
        refVideo.current.setAttribute('autoplay', true);
        refVideo.current.setAttribute('muted', true);
        refVideo.current.setAttribute('playsinline', true);
        refVideo.current.srcObject = stream;
        refVideo.current.addEventListener('loadedmetadata', () => {
          refVideo.current.play();
        });
        refHasVideo.current = true;
        const cameraSettings = stream.getTracks()[0].getSettings();
        if (window.screen.availWidth > window.screen.availHeight) {
          setDeviceSize({
            w: cameraSettings.width,
            h: cameraSettings.height
          });
        } else {
          setDeviceSize({
            w: cameraSettings.height,
            h: cameraSettings.width
          });
        }
      }
    } catch (error) {
      if (onError !== null) {
        onError(error);
      } else {
        throw error;
      }
    }
  }, [onError, setDeviceSize, refVideo, refHasVideo]);
  useEffect(() => {
    loadCamera();
  }, [loadCamera]);
  return scanning && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("video", {
    ref: refVideo,
    style: styles.videoStyle
  }), /*#__PURE__*/React.createElement("canvas", {
    ref: refCanvas,
    style: styles.containerStyle,
    width: 400,
    height: 400
  }));
}
export default MixedCodeScanner;